/**
 * The two error shapes that reach this module.
 *
 * Amplify's `RestApiError` is the one that actually arrives from every call
 * through hooks/utils/api.tsx: it carries the backend code on `name`, the HTTP
 * status on `response.statusCode` (and again on `$metadata.httpStatusCode`,
 * which buildRestApiError Object.assigns onto every instance), and leaves the
 * JSON body as a raw *string* on `response.body` — never parsed into
 * `response.data`.
 *
 * The axios-style `{ response: { status, data } }` shape is read first and
 * retained defensively — no caller in src/ produces it today (nothing imports
 * axios), but it costs one `??` and predates this module.
 */
export interface ApiError {
  $metadata?: {
    httpStatusCode?: number;
  };
  message?: string;
  name?: string;
  response?: {
    body?: unknown;
    data?: {
      code?: string;
      errorCode?: string;
      requestId?: string;
    };
    status?: number;
    statusCode?: number;
  };
}

const DEFAULT_ERROR_MESSAGE = "An unexpected error occurred. Please try again.";

const CODE_MESSAGES: Record<string, string> = {
  AUTH_REQUIRED: "Your session has expired. Please log in again.",
  CONFLICT: "This action conflicts with existing data.",
  FORBIDDEN: "You don't have permission to perform this action.",
  INTERNAL_ERROR: "Server error. Please try again later.",
  RESOURCE_NOT_FOUND:
    "Requested resource was not found or you may not have access.",
  VALIDATION_FAILED: "Invalid request. Please check your input and try again."
};

const STATUS_MESSAGES: Record<number, string> = {
  400: "Invalid request. Please check your input and try again.",
  401: "Your session has expired. Please log in again.",
  403: "You don't have permission to perform this action.",
  404: "The requested resource was not found.",
  409: "This action conflicts with existing data.",
  413: "The file is too large. Please try a smaller file.",
  422: "Unable to process your request. Please check your data.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Server error. Please try again later.",
  502: "Service temporarily unavailable. Please try again.",
  503: "Service temporarily unavailable. Please try again."
};

const ACTION_MESSAGES: Record<string, string> = {
  create: "Failed to create",
  delete: "Failed to delete",
  download: "Failed to download",
  export: "Failed to export",
  fetch: "Failed to load",
  save: "Failed to save",
  update: "Failed to update",
  upload: "Failed to upload"
};

/**
 * The backend's error body arrives as an unparsed JSON string on a thrown
 * Amplify error (mirrors mfaCodeOf in hooks/utils/api.tsx). Shape, from the
 * orm lambda:
 *
 *   { code, error, errorCode, requestId, statusCode }
 *
 * Note `errorCode` holds the human *message*, not a code — only `code` is the
 * machine code. Reading `errorCode` here would return prose where callers
 * expect FORBIDDEN/VALIDATION_FAILED, so it is deliberately ignored.
 */
type ErrorBody = {
  code?: string;
  errorCode?: string;
  requestId?: string;
  statusCode?: number;
};

const parseErrorBody = (body: unknown): ErrorBody | undefined => {
  if (typeof body !== "string") {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(body);
    return parsed && typeof parsed === "object"
      ? (parsed as ErrorBody)
      : undefined;
  } catch {
    return undefined;
  }
};

const getErrorCode = (
  apiError: ApiError,
  body: ErrorBody | undefined
): string | undefined =>
  apiError.response?.data?.errorCode ||
  apiError.response?.data?.code ||
  // Prefer the body over `name`: it carries the specific code (e.g.
  // MFA_ENROLMENT_REQUIRED) where `name` has only the generic one (FORBIDDEN).
  body?.code ||
  // Amplify puts the backend code here. Harmless for a plain JS error: "Error"
  // and "TypeError" match nothing in CODE_MESSAGES or the Sentry drop sets.
  apiError.name;

const getStatusCode = (
  apiError: ApiError,
  body: ErrorBody | undefined
): number | undefined =>
  apiError.response?.status ??
  apiError.response?.statusCode ??
  // Amplify copies the status here too (buildRestApiError Object.assigns
  // $metadata onto the error), so this is a real producer, not a guess.
  apiError.$metadata?.httpStatusCode ??
  body?.statusCode;

/**
 * Backend error code, HTTP status, request id and parsed body pulled off an
 * arbitrary caught value. Shared by the Sentry noise filter, the Sentry event
 * enrichment and the user-facing toast so all three read the same fields —
 * reading only the axios shape is what left FORBIDDEN/VALIDATION_FAILED
 * unmatched in every one of them (FRONTEND-YW and siblings).
 */
export const getApiErrorMeta = (
  error: unknown
): {
  code?: string;
  data?: unknown;
  requestId?: string;
  status?: number;
} => {
  if (!error || typeof error !== "object") {
    return {};
  }
  const apiError = error as ApiError;
  const body = parseErrorBody(apiError.response?.body);
  return {
    code: getErrorCode(apiError, body),
    data: body ?? apiError.response?.data,
    requestId: body?.requestId || apiError.response?.data?.requestId,
    status: getStatusCode(apiError, body)
  };
};

const getFallbackMessage = (error: unknown, apiError: ApiError): string => {
  if (apiError.message) return apiError.message;
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return DEFAULT_ERROR_MESSAGE;
};

export const getApiErrorMessage = (error: unknown): string => {
  const apiError = error as ApiError;
  const { code: errorCode, status: statusCode } = getApiErrorMeta(error);

  return (
    (errorCode ? CODE_MESSAGES[errorCode] : undefined) ||
    (statusCode ? STATUS_MESSAGES[statusCode] : undefined) ||
    getFallbackMessage(error, apiError)
  );
};

export const getContextualErrorMessage = (
  error: unknown,
  action: string
): string => {
  const baseMessage = getApiErrorMessage(error);
  const actionMessage = ACTION_MESSAGES[action.toLowerCase()] || action;
  return `${actionMessage}: ${baseMessage}`;
};
